import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCamera } from "@fortawesome/free-solid-svg-icons";

import { DashboardLayout } from "../../Components/Layout/DashboardLayout";
import BackButton from "../../Components/BackButton";
import CustomInput from "../../Components/CustomInput";
import CustomButton from "../../Components/CustomButton";

import placeholderimage from "../../Assets/images/placeholderimage.png";
import "./style.css";

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png"];
const MAX_IMAGE_SIZE_MB = 2;

const EditProfile = () => {
  const apiUrl = process.env.REACT_APP_BASE_URL;
  const imageUrl = process.env.REACT_APP_BASE_IMAGE_URL;

  const navigate = useNavigate();
  const token = localStorage.getItem("login");

  const [formData, setFormData] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // =========================
  // Fetch Profile
  // =========================
  const fetchProfile = async () => {
    try {
      document.querySelector(".loaderBox")?.classList.remove("d-none");

      const response = await fetch(`${apiUrl}/admin/edit-profile`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (data?.status) {
        setFormData(data.data);
      } else {
        toast.error(data?.message || "Failed to load profile data.");
      }
    } catch (error) {
      console.error("Profile fetch error:", error);
      toast.error("Could not load profile. Please check your connection.");
    } finally {
      document.querySelector(".loaderBox")?.classList.add("d-none");
    }
  };

  useEffect(() => {
    document.title = "Virtual PI | Edit Profile";
    fetchProfile();
  }, []);

  // =========================
  // Handle Input Change
  // =========================
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear the inline error as soon as the user starts correcting it
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // =========================
  // Handle Image Upload
  // =========================
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      toast.error("Invalid file type. Please upload a JPG, JPEG, or PNG image.");
      event.target.value = "";
      return;
    }

    if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
      toast.error(`Image must be smaller than ${MAX_IMAGE_SIZE_MB}MB.`);
      event.target.value = "";
      return;
    }

    const previewURL = URL.createObjectURL(file);

    setFormData((prev) => ({
      ...prev,
      image: file,
      imageFile: previewURL,
    }));
  };

  // Cleanup blob URL on unmount / image change
  useEffect(() => {
    return () => {
      if (formData?.imageFile?.startsWith("blob:")) {
        URL.revokeObjectURL(formData.imageFile);
      }
    };
  }, [formData?.imageFile]);

  // =========================
  // Client-side Validation
  // =========================
  const validate = () => {
    const errs = {};
    const name = formData?.name?.trim() ?? "";

    if (!name) {
      errs.name = "Name is required.";
    } else if (name.length < 2) {
      errs.name = "Name must be at least 2 characters.";
    } else if (name.length > 50) {
      errs.name = "Name must not exceed 50 characters.";
    }

    return errs;
  };

  // =========================
  // Submit Profile
  // =========================
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      return;
    }

    const body = new FormData();

    Object.keys(formData).forEach((key) => {
      const value = formData[key];

      // Skip frontend-only fields
      if (["imageFile", "created_at", "updated_at", "id"].includes(key)) return;

      if (key === "image") {
        if (value instanceof File) {
          body.append("image", value);
        }
      } else {
        body.append(key, value ?? "");
      }
    });

    setSubmitting(true);
    document.querySelector(".loaderBox")?.classList.remove("d-none");

    try {
      const response = await fetch(`${apiUrl}/admin/update-profile`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body,
      });

      const data = await response.json();

      if (data?.status) {
        toast.success(data.message || "Profile updated successfully.");
        navigate("/profile");
      } else {
        toast.error(data?.message || "Failed to update profile. Please try again.");
      }
    } catch (error) {
      console.error("Update error:", error);
      toast.error("Something went wrong. Please check your connection and try again.");
    } finally {
      setSubmitting(false);
      document.querySelector(".loaderBox")?.classList.add("d-none");
    }
  };

  // =========================
  // Image Source
  // =========================
  const imageSrc =
    formData?.imageFile ||
    (formData?.image ? `${imageUrl}/${formData.image}` : placeholderimage);

  return (
    <DashboardLayout>
      <div className="dashCard mb-4">
        <div className="row mb-3">
          <div className="col-12">
            <h2 className="mainTitle">
              <BackButton />
              Edit Profile
            </h2>
          </div>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="row mb-3">
            <div className="col-lg-4 mb-3">
              <div className="profileImage">
                <img
                  src={imageSrc}
                  className="img-fluid mt-2"
                  alt="User"
                />

                <input
                  type="file"
                  accept="image/jpeg,image/png"
                  className="d-none"
                  id="profileImage"
                  onChange={handleFileChange}
                />

                <label htmlFor="profileImage" className="imageUploadButton">
                  <FontAwesomeIcon icon={faCamera} />
                </label>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-6">

              <CustomInput
                label="Name"
                labelClass="mainLabel"
                value={formData?.name || ""}
                required
                type="text"
                placeholder="Enter Name"
                inputClass="mainInput"
                onChange={handleChange}
                name="name"
              />
              {fieldErrors.name && (
                <p className="text-danger small mt-1 mb-2">{fieldErrors.name}</p>
              )}

              <CustomInput
                label="Email Address"
                labelClass="mainLabel"
                value={formData?.email || ""}
                required
                type="email"
                placeholder="Enter email"
                inputClass="mainInput"
                onChange={handleChange}
                name="email"
                disabled
              />

            </div>

            <div className="col-12 mt-3">
              <CustomButton
                variant="primaryButton"
                text={submitting ? "Saving..." : "Submit"}
                type="submit"
                className="me-3 mb-2"
                disabled={submitting}
              />

              <CustomButton
                type="button"
                variant="secondaryButton"
                text="Cancel"
                className="me-3 mb-2"
                onClick={() => navigate("/profile")}
              />
            </div>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default EditProfile;
